import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { OverlayPage } from '@/components/overlay-page';
import { EmptyState } from '@/components/page-header';
import { StatusBadge } from '@/components/sales/status-badge';
import { VoidDialog } from '@/components/sales/void-dialog';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { exponentOf, rateLine } from '@/lib/currencies';
import { formatAmount, formatDate } from '@/lib/format';
import { formatMinor, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { useVendorPayment, type VendorPayment } from '@/lib/purchases';
import { PAYMENT_METHODS } from '@/lib/sales';

export const Route = createFileRoute('/expenses/payments/$paymentId')({ component: View });

/** A bill payment (E5), read-only, with void. */
function View() {
  const { paymentId } = Route.useParams();
  const orgId = useOrgId();
  const p = useVendorPayment(paymentId);
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [voiding, setVoiding] = useState(false);
  const voidM = useMutation({
    mutationFn: ({ date, reason }: { date: string; reason: string }) =>
      api<VendorPayment>(`/orgs/${orgId}/purchases/payments/${paymentId}/void`, {
        method: 'POST',
        body: JSON.stringify({ date, reason }),
      }),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      toast(`Bill payment ${r.number} voided.`);
      setVoiding(false);
    },
  });
  const close = () => void navigate({ to: '/expenses' });
  if (!orgId) return <EmptyState title="Select a client" />;
  if (p.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (p.isError) return <EmptyState title="Bill payment not found" hint={p.error.message} />;
  const d = p.data;
  // multi-currency: amounts are in the payment's currency (migration 0900)
  const exp = exponentOf(d.currency);
  const applied = d.allocations.reduce((s, a) => s + (toMinor(a.amount, exp) ?? 0n), 0n);
  return (
    <OverlayPage
      title={`Bill payment ${d.number}`}
      subtitle={<StatusBadge kind="vendor_payment" status={d.status} overdue={false} />}
      onClose={close}
      footerLeft={
        <Button variant="outline" onClick={close}>
          Close
        </Button>
      }
      footerCentre={
        <Button variant="ghost" asChild>
          <Link to="/accounting/journal-entries/$entryId" params={{ entryId: d.journalEntryId }}>
            Transaction journal
          </Link>
        </Button>
      }
      footerRight={
        d.status === 'posted' && me.data?.isStaff ? (
          <Button variant="destructive" onClick={() => setVoiding(true)}>
            Void
          </Button>
        ) : null
      }
    >
      <div className="mx-auto flex max-w-[1000px] flex-col gap-5">
        {d.status === 'void' ? (
          <div className="rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground">
            This bill payment is void{d.voidReason ? `: ${d.voidReason}` : ''}. Its posting was
            reversed and the bills it paid are open again.
          </div>
        ) : null}
        <dl className="grid grid-cols-[200px_1fr] gap-x-6 gap-y-2 rounded-lg border bg-card p-5 text-sm">
          <dt className="text-muted-foreground">Payee</dt>
          <dd>
            <Link
              to="/expenses/vendors/$contactId"
              params={{ contactId: d.contactId }}
              className="text-primary hover:underline"
            >
              {d.contactName}
            </Link>
          </dd>
          <dt className="text-muted-foreground">Payment date</dt>
          <dd>{formatDate(d.date)}</dd>
          <dt className="text-muted-foreground">Payment account</dt>
          <dd>{d.paymentAccountName}</dd>
          <dt className="text-muted-foreground">Payment method</dt>
          <dd>{PAYMENT_METHODS.find((m) => m.value === d.method)?.label ?? '—'}</dd>
          <dt className="text-muted-foreground">Cheque / ref no.</dt>
          <dd>{d.reference ?? '—'}</dd>
          <dt className="text-muted-foreground">Amount paid</dt>
          <dd className="tnum font-semibold">
            {d.currency} {formatAmount(d.amount)}
          </dd>
          {d.currency !== d.homeCurrency ? (
            <>
              <dt className="text-muted-foreground">Exchange rate</dt>
              <dd className="tnum">{rateLine(d.currency, d.fxRate, d.homeCurrency)}</dd>
              <dt className="text-muted-foreground">Amount paid in {d.homeCurrency}</dt>
              <dd className="tnum">
                {d.homeCurrency} {formatAmount(d.homeAmount)}
              </dd>
            </>
          ) : null}
          <dt className="text-muted-foreground">Unapplied (credit with the vendor)</dt>
          <dd className="tnum">
            {d.currency} {formatAmount(d.unapplied)}
          </dd>
          <dt className="text-muted-foreground">Memo</dt>
          <dd>{d.memo ?? '—'}</dd>
        </dl>
        <section className="rounded-lg border bg-card">
          <h2 className="border-b px-4 py-2 text-sm font-semibold">Bills paid</h2>
          {d.allocations.length ? (
            <ul className="divide-y text-sm">
              {d.allocations.map((a) => (
                <li key={a.id} className="flex justify-between px-4 py-2">
                  {a.type === 'journal_entry' ? (
                    <span>Journal entry {a.refNumber}</span>
                  ) : (
                    <Link
                      to={`/expenses/bills/${a.refId}`}
                      className="text-primary hover:underline"
                    >
                      Bill {a.refNumber}
                    </Link>
                  )}
                  <span className="tnum text-right">
                    {formatDate(a.date)} · {d.currency} {formatAmount(a.amount)}
                    {a.exchangeGainLoss ? (
                      <span className="block text-xs text-muted-foreground">
                        Exchange {a.exchangeGainLoss.startsWith('-') ? 'loss' : 'gain'}{' '}
                        {d.homeCurrency} {formatAmount(a.exchangeGainLoss.replace('-', ''))}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
              <li className="flex justify-between px-4 py-2 font-medium">
                <span>Total applied</span>
                <span className="tnum">
                  {d.currency} {formatMinor(applied, exp)}
                </span>
              </li>
            </ul>
          ) : (
            <p className="px-4 py-3 text-sm text-muted-foreground">Not applied to any bill.</p>
          )}
        </section>
      </div>
      <VoidDialog
        open={voiding}
        title={`Void bill payment ${d.number}`}
        description="Voiding posts a reversing entry and reopens the bills this payment settled. This cannot be undone."
        minDate={d.date}
        pending={voidM.isPending}
        error={voidM.error ? voidM.error.message : null}
        onCancel={() => setVoiding(false)}
        onConfirm={(date, reason) => voidM.mutate({ date, reason })}
      />
    </OverlayPage>
  );
}
